import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { useUserContext } from './useUserContext';

/**
 * Interface for a single feature flag
 */
export interface FeatureFlag {
  enabled: boolean;
  access_result: 'granted' | 'denied' | 'override_granted';
  reason: string;
  display_name: string;
  description?: string;
  feature_type: string;
  category?: string;
  minimum_role?: string;
  has_override: boolean;
  override_reason?: string;
  override_expires?: string;
}

/**
 * Response from the feature flags API
 */
export interface FeatureFlagsResponse {
  [key: string]: FeatureFlag;
}

/**
 * Hook to fetch all feature flags for the current user
 * Enhanced with aggressive cache management for immediate feature flag updates
 * Uses placeholderData to eliminate flicker when navigating between cached pages
 */
export const useFeatureFlags = () => {
  const { accessToken, isApiAuthReady } = useAuthStore();
  const { userId } = useUserContext();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['feature-flags', userId], // User-aware cache key for security
    queryFn: async (): Promise<FeatureFlagsResponse> => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[useFeatureFlags] Fetching feature flags from API...');
      }
      
      const response = await fetch('/api/v1/feature-flags/user', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch feature flags');
      }
      
      const data = await response.json();
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[useFeatureFlags] API response received:', Object.keys(data));
      }
      
      return data;
    },
    enabled: !!isApiAuthReady && !!userId,
    staleTime: 10 * 60 * 1000, // ✅ 10 minutes - feature flags rarely change
    gcTime: 30 * 60 * 1000, // ✅ 30 minutes - keep in cache longer
    refetchOnWindowFocus: false, // ✅ Don't refetch on focus - feature flags are stable
    refetchOnMount: false, // ✅ Don't refetch on mount - use cached data
    refetchOnReconnect: false, // ✅ Don't refetch on reconnect - not critical data
    retry: 2,
    // ✅ FLICKER FIX: Provide cached data immediately during remounts
    // This prevents the loading state from flashing when navigating back to previously visited pages
    placeholderData: () => {
      const cachedData = queryClient.getQueryData<FeatureFlagsResponse>(['feature-flags', userId]);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[useFeatureFlags] placeholderData check:', {
          hasCachedData: !!cachedData,
          cachedKeys: cachedData ? Object.keys(cachedData) : [],
          userId
        });
      }
      
      return cachedData as FeatureFlagsResponse | undefined;
    },
  });
};

/**
 * Hook to check access to a specific feature flag
 * Enhanced to expose fetching state and raw data for better loading UX
 * 
 * @param featureKey - The feature flag key to check
 * @returns {object} Feature flag state
 * @returns {boolean} isEnabled - Whether user has access
 * @returns {boolean} isLoading - True only when NO cached data exists
 * @returns {boolean} hasData - True if flags loaded (even from cache)
 * 
 * LOADING STRATEGY:
 * - First visit: isLoading=true (300ms API call)
 * - Cached visit: hasData=true immediately (instant)
 * - Use hasData to determine if you can trust isEnabled
 */
export const useFeatureFlag = (featureKey: string) => {
  const { data: flags, isLoading, isFetching, error } = useFeatureFlags();
  
  const feature = flags && flags[featureKey] ? flags[featureKey] : undefined;
  
  // ✅ FIX: hasData should check if we have the flags object, not just the specific feature
  // Even if the feature doesn't exist, having the flags object means we've loaded data
  const hasData = !!flags;
  
  // Debug logging to track loading states
  if (process.env.NODE_ENV === 'development') {
    console.log(`[useFeatureFlag:${featureKey}] State:`, {
      hasFlags: !!flags,
      hasFeature: !!feature,
      isLoading,
      isFetching,
      hasData,
      shouldShowLoading: isLoading && !hasData
    });
  }
  
  return {
    isEnabled: feature?.enabled ?? false,
    isLoading,
    isFetching, // ✅ NEW: Expose background fetching state
    hasData, // ✅ FIXED: Indicates if we have flags data loaded (even if cached)
    error,
    accessResult: feature?.access_result,
    reason: feature?.reason,
    displayName: feature?.display_name,
    description: feature?.description,
    minimumRole: feature?.minimum_role,
    hasOverride: feature?.has_override ?? false,
    overrideReason: feature?.override_reason,
    overrideExpires: feature?.override_expires,
    category: feature?.category,
    featureType: feature?.feature_type,
  };
};

/**
 * Hook to check multiple feature flags at once
 */
export const useMultipleFeatureFlags = (featureKeys: string[]) => {
  const { data: allFlags, isLoading } = useFeatureFlags();
  
  const results = featureKeys.reduce((acc, key) => {
    const feature = allFlags && allFlags[key] ? allFlags[key] : undefined;
    acc[key] = {
      isEnabled: feature?.enabled ?? false,
      accessResult: feature?.access_result,
      reason: feature?.reason,
      displayName: feature?.display_name,
      minimumRole: feature?.minimum_role,
    };
    return acc;
  }, {} as Record<string, { 
    isEnabled: boolean; 
    accessResult?: string; 
    reason?: string;
    displayName?: string;
    minimumRole?: string;
  }>);
  
  return {
    flags: results,
    isLoading,
    allEnabled: featureKeys.every(key => results[key]?.isEnabled),
    anyEnabled: featureKeys.some(key => results[key]?.isEnabled),
    enabledCount: featureKeys.filter(key => results[key]?.isEnabled).length,
    disabledCount: featureKeys.filter(key => !results[key]?.isEnabled).length,
  };
};

/**
 * Hook to check if user has access to any feature in a category
 */
export const useFeatureFlagsByCategory = (category: string) => {
  const { data: allFlags, isLoading } = useFeatureFlags();
  
  const categoryFlags = allFlags ? Object.entries(allFlags)
    .filter(([_, flag]) => (flag as FeatureFlag).category === category)
    .reduce((acc, [key, flag]) => {
      acc[key] = flag as FeatureFlag;
      return acc;
    }, {} as Record<string, FeatureFlag>) : {};
  
  const enabledFlags = Object.entries(categoryFlags)
    .filter(([_, flag]) => flag.enabled)
    .reduce((acc, [key, flag]) => {
      acc[key] = flag;
      return acc;
    }, {} as Record<string, FeatureFlag>);
  
  return {
    categoryFlags,
    enabledFlags,
    isLoading,
    hasAnyEnabled: Object.keys(enabledFlags).length > 0,
    hasAllEnabled: Object.keys(categoryFlags).length > 0 && 
                   Object.keys(enabledFlags).length === Object.keys(categoryFlags).length,
    enabledCount: Object.keys(enabledFlags).length,
    totalCount: Object.keys(categoryFlags).length,
  };
};

/**
 * Hook to specifically check feature access and get upgrade information
 */
export const useFeatureAccess = (featureKey: string) => {
  const { isEnabled, accessResult, reason, minimumRole, displayName } = useFeatureFlag(featureKey);
  
  const needsUpgrade = accessResult === 'denied' && reason?.includes('Insufficient role');
  const isGloballyDisabled = accessResult === 'denied' && reason?.includes('globally disabled');
  const hasOverrideAccess = accessResult === 'override_granted';
  
  return {
    isEnabled,
    needsUpgrade,
    isGloballyDisabled,
    hasOverrideAccess,
    minimumRole,
    displayName,
    reason,
    upgradeMessage: needsUpgrade ? `Upgrade to ${minimumRole} to access ${displayName}` : null,
  };
};

/**
 * Pre-configured hooks for common features
 */
export const usePortfolioOptimization = () => useFeatureFlag('portfolio_optimization');
export const usePortfolioCommandCenter = () => useFeatureFlag('portfolio_command_center');
export const useAdvancedCharts = () => useFeatureFlag('advanced_charts');
export const useExportFunctionality = () => useFeatureFlag('export_functionality');
export const useRealTimeNotifications = () => useFeatureFlag('real_time_notifications');
export const useAdminPanel = () => useFeatureFlag('admin_panel');
export const useStrategyDetails = () => useFeatureFlag('strategy_details');

/**
 * Hook for conditional API calls based on feature flags
 */
export const useConditionalQuery = <T>(
  featureKey: string,
  queryKey: string[],
  queryFn: () => Promise<T>,
  options?: any
) => {
  const { isEnabled } = useFeatureFlag(featureKey);
  const { isApiAuthReady } = useAuthStore();
  
  return useQuery({
    queryKey,
    queryFn,
    enabled: !!isApiAuthReady && isEnabled,
    ...options,
  });
};

/**
 * Type guard to check if a feature flag response is valid
 */
export const isValidFeatureFlag = (flag: any): flag is FeatureFlag => {
  return flag && 
    typeof flag.enabled === 'boolean' &&
    typeof flag.access_result === 'string' &&
    typeof flag.reason === 'string' &&
    typeof flag.display_name === 'string';
};